/**
 * Date utilities
 */
import { format, parse, isValid, differenceInYears } from 'date-fns';

/**
 * Calculates age from date of birth
 * @param {Date|string} dateOfBirth - Date of birth
 * @returns {number} - Age in years
 */
export const calculateAge = (dateOfBirth) => {
  if (!dateOfBirth) return 0;
  
  const today = new Date();
  const dob = new Date(dateOfBirth);
  
  if (!isValid(dob)) return 0;
  
  return differenceInYears(today, dob);
};

/**
 * Formats date to SQL format (YYYY-MM-DD)
 * @param {Date|string} date - Date to format
 * @returns {string|null} - Formatted date or null
 */
export const toSqlDate = (date) => {
  if (!date) return null;
  
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    if (!isValid(dateObj)) return null;
    
    return format(dateObj, 'yyyy-MM-dd');
  } catch (error) {
    console.warn('Error formatting date to SQL:', error);
    return null;
  }
};

/**
 * Formats date for display (DD/MM/YYYY)
 * @param {Date|string} date - Date to format
 * @returns {string} - Formatted date
 */
export const formatDateForDisplay = (date) => {
  if (!date) return '';
  
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    if (!isValid(dateObj)) return '';
    
    return format(dateObj, 'dd/MM/yyyy');
  } catch (error) {
    console.warn('Error formatting date for display:', error);
    return '';
  }
};

/**
 * Parses date string in DD/MM/YYYY format
 * @param {string} dateString - Date string to parse
 * @returns {Date|null} - Parsed date or null
 */
export const parseDateString = (dateString) => {
  if (!dateString) return null;
  
  try {
    const parsed = parse(dateString, 'dd/MM/yyyy', new Date());
    return isValid(parsed) ? parsed : null;
  } catch (error) {
    console.warn('Error parsing date string:', error);
    return null;
  }
};

/**
 * Checks if date is in the past
 * @param {Date|string} date - Date to check
 * @returns {boolean} - True if date is in the past
 */
export const isPastDate = (date) => {
  if (!date) return false;
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return isValid(dateObj) && dateObj < new Date();
};

/**
 * Checks if date is in the future
 * @param {Date|string} date - Date to check
 * @returns {boolean} - True if date is in the future
 */
export const isFutureDate = (date) => {
  if (!date) return false;
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return isValid(dateObj) && dateObj > new Date();
};
